
import { createTheme, alpha } from "@mui/material/styles";
import themeService from "./services/themeService";

// Un set de colores por cada modo de tema: fondos, texto e inputs siempre
// verificando contraste AA (4.5:1 texto normal, 3:1 texto grande/UI) contra
// su propio fondo. "primary"/"accent" son el color de marca/AppBar/botones
// de cada modo — se mantienen iguales (azul) en "blue"/"graphite"/"light"
// por identidad visual, pero "dark" es la excepción a propósito: es el modo
// sin nada de azul (pedido explícito), así que ahí son neutros.
const PALETTES = {
  // Azul Predeterminado: el look de marca de Notiline (fondo azul-marino
  // oscuro). Tema aparte para elegir, ya no es el modo por defecto.
  blue: {
    mode: 'dark',
    primary: '#030553',
    primaryHover: '#050870',
    accent: '#a0b4ff',
    action: '#a0b4ff',
    actionHover: '#b8c8ff',
    onAction: '#0a0a1a',
    backgroundDefault: '#10152f',
    backgroundPaper: '#1a2149',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.75)',
    inputBackground: '#212a56',
    inputBackgroundHover: '#293568',
    inputBorder: 'rgba(255,255,255,0.38)',
    inputLabel: 'rgba(255,255,255,0.75)',
    switchTrackOff: 'rgba(255,255,255,0.3)',
    divider: 'rgba(255,255,255,0.14)',
  },
  // Modo Oscuro: negro, sin nada de azul — ni fondos, ni AppBar/bottom nav,
  // ni botones ni accent. Reemplaza al "Modo Oscuro" anterior, que ahora
  // vive en la clave "graphite" con otro nombre ("Gris Oscuro").
  dark: {
    mode: 'dark',
    primary: '#000000',
    primaryHover: '#1a1a1a',
    accent: '#e0e0e0',
    action: '#e0e0e0',
    actionHover: '#f0f0f0',
    onAction: '#0a0a1a',
    backgroundDefault: '#0a0a0a',
    backgroundPaper: '#1a1a1a',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.72)',
    inputBackground: '#262626',
    inputBackgroundHover: '#333333',
    inputBorder: 'rgba(255,255,255,0.35)',
    inputLabel: 'rgba(255,255,255,0.72)',
    switchTrackOff: 'rgba(255,255,255,0.3)',
    divider: 'rgba(255,255,255,0.12)',
  },
  // Gris Oscuro: gris neutro con acentos azules de marca. Es el modo por
  // defecto para cuentas nuevas.
  graphite: {
    mode: 'dark',
    primary: '#030553',
    primaryHover: '#050870',
    accent: '#a7b8ff',
    action: '#a7b8ff',
    actionHover: '#bccaff',
    onAction: '#0a0a1a',
    backgroundDefault: '#121212',
    backgroundPaper: '#1e1e1e',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.72)',
    inputBackground: '#2c2c2c',
    inputBackgroundHover: '#383838',
    inputBorder: 'rgba(255,255,255,0.38)',
    inputLabel: 'rgba(255,255,255,0.72)',
    switchTrackOff: 'rgba(255,255,255,0.3)',
    divider: 'rgba(255,255,255,0.12)',
  },
  // Modo Claro: fondos claros, texto oscuro. El "accent" se oscurece
  // respecto a los modos oscuros porque el celeste claro original
  // (#a0b4ff) no cumple contraste sobre fondo blanco.
  light: {
    mode: 'light',
    primary: '#030553',
    primaryHover: '#050870',
    accent: '#1a237e',
    action: '#030553',
    actionHover: '#050870',
    onAction: '#ffffff',
    backgroundDefault: '#f2f3f7',
    backgroundPaper: '#ffffff',
    textPrimary: '#15171f',
    textSecondary: 'rgba(21,23,31,0.72)',
    inputBackground: '#eef0f5',
    inputBackgroundHover: '#e3e6ee',
    inputBorder: 'rgba(0,0,0,0.28)',
    inputLabel: 'rgba(21,23,31,0.65)',
    switchTrackOff: 'rgba(0,0,0,0.28)',
    divider: 'rgba(0,0,0,0.12)',
  },
};

export const getBrandColors = (mode = themeService.getResolvedMode()) =>
  PALETTES[mode] || PALETTES[themeService.getResolvedMode()] || PALETTES.graphite;

// Colores para AppBars y barras de navegación inferior. En Modo Oscuro y
// Azul Predeterminado siguen siendo azul de marca (identidad visual); en
// Modo Claro pasan a blanco/claro para no quedar como una franja oscura
// aislada sobre un fondo claro (pedido explícito: appbar y bottom nav
// "claras/blancas" en modo claro).
export const getBarColors = (mode = themeService.getResolvedMode()) => {
  const p = getBrandColors(mode);
  if (p.mode === 'light') {
    return {
      background: p.backgroundPaper,
      text: p.textPrimary,
      textMuted: p.textSecondary,
      border: p.divider,
      activeBg: alpha(p.primary, 0.08),
      avatarBorder: 'rgba(0,0,0,0.25)',
      // Botones de la bottom nav en negro en Modo Claro (pedido explícito),
      // en vez del accent azul que se usa en los demás modos.
      icon: '#000000',
    };
  }
  return {
    background: p.primary,
    text: p.textPrimary,
    textMuted: 'rgba(255,255,255,0.7)',
    border: 'rgba(255,255,255,0.08)',
    activeBg: alpha(p.accent, 0.16),
    avatarBorder: 'rgba(255,255,255,0.3)',
    // Íconos de la bottom nav: en Azul Predeterminado usan el accent (azul
    // claro de marca, pedido explícito); en Gris Oscuro y Modo Oscuro son
    // blancos puros en vez del accent (pedido explícito), ya que ninguno de
    // los dos usa azul como identidad de íconos.
    icon: mode === 'blue' ? p.accent : '#ffffff',
  };
};

export const buildTheme = (mode = themeService.getResolvedMode()) => {
  const p = getBrandColors(mode);
  const bar = getBarColors(mode);
  const onPrimary = '#ffffff';

  return createTheme({
    palette: {
      mode: p.mode,
      primary: {
        main: p.primary,
        dark: p.primaryHover,
        contrastText: onPrimary,
      },
      background: {
        default: p.backgroundDefault,
        paper: p.backgroundPaper,
      },
      text: {
        primary: p.textPrimary,
        secondary: p.textSecondary,
      },
      divider: p.divider,
    },
    typography: {
      fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: "none",
          },
          // En los modos oscuros el fondo de marca (#030553 / #000) casi no se
          // distingue de la página: el botón principal va con el color de acción.
          containedPrimary: {
            backgroundColor: p.action,
            color: p.onAction,
            "&:hover": { backgroundColor: p.actionHover },
          },
          outlinedPrimary: {
            color: p.accent,
            // Borde con el accent (no con primary, que en los modos oscuros
            // es casi igual al fondo y el botón quedaba sin contorno).
            borderColor: alpha(p.accent, 0.5),
            "&:hover": {
              borderColor: p.primary,
              backgroundColor: p.primary,
              // El hover siempre tiene fondo azul de marca (oscuro en los 3
              // modos), asi que el texto en hover es siempre blanco, no
              // brandColors.textPrimary (que en modo claro es oscuro y
              // quedaria ilegible sobre ese fondo).
              color: onPrimary,
            },
          },
          textPrimary: {
            color: p.accent,
            "&:hover": {
              backgroundColor: alpha(p.primary, 0.15),
            },
          },
        },
      },
      // Los links usaban palette.primary (#030553): azul marino ilegible
      // sobre los fondos oscuros. Igual que los botones de texto, van con el
      // accent de cada modo (celeste en los oscuros, azul oscuro en claro).
      MuiLink: {
        styleOverrides: {
          root: {
            color: p.accent,
            textDecorationColor: alpha(p.accent, 0.5),
            "&:hover": {
              textDecorationColor: p.accent,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: p.backgroundPaper,
            color: p.textPrimary,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: bar.background,
            color: bar.text,
            backgroundImage: 'none',
            borderTop: 'none',
            borderBottom: 'none',
          },
          // AppBar con color="primary" (el default cuando no se especifica
          // color) genera además esta clase, que sin este override pisaría
          // el backgroundColor de arriba y dejaría la barra siempre navy.
          colorPrimary: {
            backgroundColor: bar.background,
            color: bar.text,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: p.backgroundPaper,
            // Sin esto, MUI le suma en modo oscuro un degradado blanco
            // translucido segun la elevacion (mas fuerte en Dialog/Menu/Popover,
            // que usan elevaciones altas), aclarando el panel por encima del
            // fondo de superficie en vez de dejarlo plano.
            backgroundImage: 'none',
            color: p.textPrimary,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: p.backgroundPaper,
            color: p.textPrimary,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: p.backgroundPaper,
            color: p.textPrimary,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: p.textPrimary,
            backgroundColor: p.inputBackground,
            borderRadius: 8,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: p.inputBackground,
            borderRadius: 8,
            // Al enfocar, MUI pinta el borde con palette.primary (#030553):
            // azul marino que casi no se ve sobre los fondos oscuros. Va con
            // el accent, igual que los links y los switches. Tiene que ir en
            // root: OutlinedInput no aplica una clave "focused" del override.
            '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: p.accent,
            },
          },
          notchedOutline: {
            borderColor: p.inputBorder,
          },
          // Chrome/Edge pintan el fondo de autofill (nombre, teléfono,
          // contraseñas guardadas) con su propio celeste/amarillo por
          // encima del tema; el truco del box-shadow interno enorme lo tapa
          // sin afectar el resto del input.
          input: {
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 100px ${p.inputBackground} inset`,
              WebkitTextFillColor: p.textPrimary,
              caretColor: p.textPrimary,
              borderRadius: 8,
              transition: 'background-color 5000s ease-in-out 0s',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: p.inputLabel,
          },
        },
      },
      // El título del campo enfocado (y de los grupos de radios/checkboxes)
      // también usaba palette.primary; mismo cambio que el borde de arriba.
      MuiFormLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused:not(.Mui-error)': {
              color: p.accent,
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            '&:not(.Mui-error):after': {
              borderBottomColor: p.accent,
            },
          },
        },
      },
      // Radios y checkboxes marcados (Público/Privado, etc.) también salían
      // en palette.primary, casi invisibles sobre fondo oscuro.
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: p.accent,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked, &.MuiCheckbox-indeterminate': {
              color: p.accent,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: p.inputBackground,
            '&:hover': {
              backgroundColor: p.inputBackgroundHover,
            },
            '&.Mui-focused': {
              backgroundColor: p.inputBackground,
            },
          },
          underline: {
            '&:not(.Mui-error):after': {
              borderBottomColor: p.accent,
            },
          },
          input: {
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 100px ${p.inputBackground} inset`,
              WebkitTextFillColor: p.textPrimary,
              caretColor: p.textPrimary,
              transition: 'background-color 5000s ease-in-out 0s',
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: p.textPrimary,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          // El azul de palette.primary (#030553) casi no se distingue del
          // fondo cuando el switch está activado; se usa el accent (mas
          // claro en modos oscuros, mas oscuro en modo claro) para que el
          // estado "on" tenga contraste real contra su propio fondo.
          track: {
            backgroundColor: p.switchTrackOff,
            opacity: 1,
          },
          switchBase: {
            '&.Mui-checked': {
              color: p.accent,
            },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: p.accent,
              opacity: 0.6,
            },
            '&.Mui-checked:hover': {
              backgroundColor: alpha(p.accent, 0.12),
            },
          },
        },
      },
    },
  });
};

// Se calculan una sola vez al cargar el módulo, con el modo guardado en
// localStorage. El cambio de tema recarga la página (mismo patrón que el
// selector de idioma en Idioma.js), así que no hace falta que estos valores
// sean reactivos dentro de una misma sesión.
export const brandColors = getBrandColors();
export const barColors = getBarColors();
// Clave del tema ya resuelta ('blue' | 'dark' | 'graphite' | 'light'; 'auto'
// nunca se expone aquí), a diferencia de brandColors.mode que solo distingue
// 'dark' vs 'light' (útil para elegir entre variantes de un mismo modo
// oscuro, como el logo de Modo Oscuro).
export const themeMode = themeService.getResolvedMode();
const theme = buildTheme();

export default theme;
